<template>
  <div class="pb-1">
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="6">
            <FormulateInput
              id="partner-name"
              v-model="filters.partnerName"
              :label="$t('Nome do parceiro')"
              :placeholder="$t('Nome')"
            />
          </b-col>
          <b-col md="6">
            <FormulateInput
              id="partnership-cnpj"
              v-model="filters.cnpj"
              :label="$t('CNPJ')"
              :placeholder="$t('CNPJ')"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <FormulateInput
              id="partnership-partner-type"
              v-model="filters.partnerType"
              type="vue-select"
              :label="$t('Tipo de parceiro')"
              :placeholder="$t('Selecione o tipo')"
              :options="partnerTypeOptions"
            />
          </b-col>
          <b-col md="6">
            <FormulateInput
              id="partnership-active"
              v-model="filters.active"
              type="vue-select"
              :label="$t('Status')"
              :placeholder="$t('Todos')"
              :options="statusOptions"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>
      <b-table
        ref="partnerships-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('PARTNERSHIP.NAME'), 'female', queryExecuted)"
        :fields="fields"
        :items="partnerships"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(cnpj)="row">
          <span>{{ row.item.cnpj | cpfCnpj }}</span>
        </template>

        <template #cell(estimatedParticipants)="row">
          <span class="text-right">{{ row.item.estimatedParticipants }}</span>
        </template>

        <template #cell(priceTableName)="row">
          <span>{{ row.item.priceTableName || '-' }}</span>
        </template>

        <template #cell(partnershipUrl)="row">
          <span
            v-if="row.item.partnershipUrl"
            class="text-truncate"
            style="max-width: 180px; display: inline-block"
            :title="row.item.partnershipUrl"
          >
            {{ row.item.partnershipUrl }}
          </span>
          <span v-else>-</span>
        </template>

        <template #cell(active)="row">
          <e-status-badge :status="row.item.active" />
        </template>

        <template #cell(action)="row">
          <e-grid-actions
            :is-active="row.item.active"
            :show-update="$can('Update', 'Partnership')"
            :show-delete="$can('Delete', 'Partnership')"
            :show-activate="$can('Activate', 'Partnership')"
            :show-deactivate="$can('Deactivate', 'Partnership')"
            :show-read-only="true"
            @update="showUpdatePartnershipSidebar(row)"
            @delete="deletePartnership(row)"
            @activate="activateDeactivatePartnership(row)"
            @deactivate="activateDeactivatePartnership(row)"
            @read-only="showUpdatePartnershipSidebar(row, true)"
          />
        </template>
      </b-table>
      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="partnership-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <partnership-sidebar
      ref="partnership_sidebar"
      @save="getData"
    />

    <fab
      v-if="$can('Create', 'Partnership')"
      :main-tooltip="$t('Adicionar Parceria')"
      @click="showCreatePartnershipSidebar"
    />
  </div>
</template>

<script>
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  EPageSizeSelector,
  EPaginationSummary,
  EStatusBadge,
  EFilters,
  EGridActions,
} from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { mapActions, mapState } from 'vuex'
import PartnershipSidebar from './PartnershipSidebar.vue'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    fab,
    EStatusBadge,
    EFilters,
    PartnershipSidebar,
    EGridActions,
  },

  data() {
    return {
      queryExecuted: false,
      showSidebar: false,
      fetching: false,
      edit: false,
      saving: false,
    }
  },

  computed: {
    ...mapState('pages/partnerships', {
      partnerships: 'partnerships',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
    }),

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Id'),
          key: 'id',
          class: 'text-center',
          thStyle: { width: '80px' },
          sortable: true,
        },
        {
          label: this.$t('Tipo'),
          key: 'partnerType',
          class: 'text-center',
          thStyle: { width: '120px' },
          sortable: true,
          formatter: value => this.$t(value),
        },
        {
          label: this.$t('Nome do Parceiro'),
          key: 'partnerName',
          trClass: 'text-center',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('CNPJ'),
          key: 'cnpj',
          class: 'text-center',
          thStyle: { width: '150px' },
          sortable: true,
        },
        {
          label: this.$t('Estimativa Participantes'),
          key: 'estimatedParticipants',
          class: 'text-center',
          thStyle: { width: '160px' },
          sortable: true,
        },
        {
          label: this.$t('Campo de Verificação'),
          key: 'verificationFieldName',
          class: 'text-center',
          thStyle: { width: '150px' },
          sortable: true,
        },
        {
          label: this.$t('Tabela de Preço'),
          key: 'priceTableName',
          class: 'text-center',
          thStyle: { width: '150px' },
          sortable: true,
        },
        {
          label: this.$t('URL da Parceria'),
          key: 'partnershipUrl',
          class: 'text-center',
          thStyle: { width: '200px' },
          sortable: true,
        },
        {
          label: this.$t('Status'),
          key: 'active',
          class: 'text-center',
          thStyle: { width: '90px' },
          sortable: true,
        },
      ]
    },

    partnerTypeOptions() {
      return [
        { value: '', label: this.$t('Todos') },
        { value: 0, label: this.$t('Empresa') },
        { value: 1, label: this.$t('Condomínio') },
      ]
    },

    statusOptions() {
      return [
        { value: '', label: this.$t('Todos') },
        { value: true, label: this.$t('Ativo') },
        { value: false, label: this.$t('Inativo') },
      ]
    },
  },

  mounted() {
    this.getData()
  },

  methods: {
    ...mapActions('pages/partnerships', [
      'fetchPartnerships',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
    ]),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchPartnerships()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    showCreatePartnershipSidebar() {
      this.$refs.partnership_sidebar.show()
    },

    async showUpdatePartnershipSidebar(row, isReadOnly = false) {
      this.$refs.partnership_sidebar.show({ id: row.item.id }, isReadOnly)
    },

    async deletePartnership(partnership) {
      const { id } = partnership.item
      try {
        const confirm = await this.confirm()
        if (confirm) {
          await this.$http.delete(`/api/partnerships/${id}`)
          this.getData()
          this.showSuccess({ message: this.$t('Parceria removida com sucesso') })
        }
      } catch (error) {
        this.showError({ error })
      }
    },

    async activateDeactivatePartnership(row) {
      try {
        const confirm = await this.confirm()
        if (confirm) {
          const path = row.item.active ? 'deactivate' : 'activate'
          await this.$http({
            url: `/api/partnerships/${row.item.id}/${path}`,
            method: 'PUT',
          })
          this.getData()
          this.showSuccess({
            message: this.$t(`${row.item.active === true ? 'Inativado' : 'Ativado'} com sucesso`),
          })
        }
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style></style>
